import { Component, Input, OnChanges, OnInit, inject, viewChildren, viewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ConfirmDialogModel, LoadingOverlayComponent, ButtonComponent } from 'wecheer-common';
import { Advertiser, Campaign, CampaignsAdvertiserAdminApiClient, CampaignsCampaignAdminApiClient, ConsumerReferralStrategy, ProductReward, PunchCardFlow, PunchCardStep, PunchCardStrategyType, RewardData, SafeBox } from 'src/app/api/api-campaigns.service.generated';
import { ProductInfo, RewardsProductApiClient } from 'src/app/api/api-rewards.service.generated';
import { UniversalCodeComponent } from '../../universal-code/universal-code.component';
import { formToRewardData } from '../../_/reward-data-edit/reward-data-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PunchCardCampaignService } from '../punch-card-campaign-edit.service';
import { CampaignFlowItemComponent } from '../../_/campaign-flow-item/campaign-flow-item.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RefferalLinkSettingComponent } from '../../_/advertiser-details/advertiser-campaigns/_/refferal-link-setting/refferal-link-setting.component';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RewardDataEditComponent } from '../../_/reward-data-edit/reward-data-edit.component';
import { lastValueFrom } from 'rxjs';

interface ConsumerReferralValidation {
  invalid: boolean;
  stepValidations: any;
  steps: PunchCardStep[];
  isEnabledCompleteReward: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-consumer-referral-tab',
  templateUrl: './consumer-referral-tab.component.html',
  styleUrls: ['./consumer-referral-tab.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatExpansionModule,
    FlexLayoutModule,
    RefferalLinkSettingComponent,
    RewardDataEditComponent,
    CampaignFlowItemComponent,
    LoadingOverlayComponent,
    ButtonComponent
]
})
export class ConsumerReferralTabComponent implements OnChanges, OnInit {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private rewardProductsApi = inject(RewardsProductApiClient);
  punchCardCampaignService = inject(PunchCardCampaignService);


  readonly flowItemList = viewChildren(CampaignFlowItemComponent);
  readonly referralLinkSetting = viewChild(RefferalLinkSettingComponent);

  // @Input() strategy: PunchCardStrategy;
  @Input() advertiserId: string;
  @Input() campaignId: string;    // shared campaign id
  @Input() isPoints: boolean;
  @Input() isPunchCard: boolean;      // PunchCard campaing can have JoinReward, FirstConsumption and CompleteReward
  @Input() isConsumerInfo: boolean;   // ConsumerInformation campaign can have only JoinReward


  form = new FormGroup({
    consumerReferralCampaigns: this.fb.array([], [])
  });
  selectedChannelIndex: number = 0;

  rewardProducts: ProductReward[];
  advertiser: Advertiser;
  isLoading = false;
  isAdding: boolean;
  dataValidations: ConsumerReferralValidation[] = [];
  stepList: PunchCardStep[] = [];
  referralCampaigns: Campaign[] = [];

  ngOnInit(): void {
    this.stepList = this.punchCardCampaignService.stepList;
    this.punchCardCampaignService.onStepListChanged
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.stepList = value?.stepList;
        let referralCampaigns = this.getFormConsumerReferralCampaigns();
        this.bindingConsumerReferralSteps(referralCampaigns, value?.index, value?.step);
        if (this.punchCardCampaignService.viewMode == 'flow') {
          this.resetCompleteRewardToggles();
        }
      });
    this.punchCardCampaignService.onFlowChange
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value != undefined) {
          let referralCampaigns = this.getFormConsumerReferralCampaigns();
          this.bindingConsumerReferralSteps(referralCampaigns, value.index, value.step);
          this.punchCardCampaignService.onFlowChange.next(undefined);
          this.resetCompleteRewardToggles();
        }
      })

  }

  async ngOnChanges() {
    if (!this.advertiserId) return;

    this.getAdvertiser();
    this.getProductRewards();

    this.isAdding = !this.campaignId;
    if (!this.isAdding)
      this.getDbConsumerReferralCampaigns();
  }

  resetCompleteRewardToggles() {
    this.dataValidations?.forEach(item => {
      item.isEnabledCompleteReward = false;
    })
  }
  onCompleteRewardToggleChange(ev, index) {
    if (this.dataValidations[index]) {
      this.dataValidations[index].isEnabledCompleteReward = ev;
    }
  }

  onProductStepRewardSelected(ev, index) {
    this.dataValidations[this.selectedChannelIndex].steps[index].completeReward = new RewardData({
      productReward: ev
    });

    if (this.referralCampaigns[this.selectedChannelIndex]?.strategy?.flow?.steps[index]) {
      this.referralCampaigns[this.selectedChannelIndex].strategy.flow.steps[index].completeReward = new RewardData({
        productReward: ev
      });
    }

  }

  async getProductRewards() {
    try {
      const productInfos = await lastValueFrom(this.rewardProductsApi.getProductsByAdvertiserId(this.advertiserId, false, false));
      this.rewardProducts = productInfos.map((pi: ProductInfo) =>
        new ProductReward({ productSlug: pi.slug, rewardTitle: pi.name, rewardDescription: pi.description, rewardThumbUrl: pi.photoUrl, rewardPhotoUrl: pi.photoUrl })
      );

    } finally {
      this.isLoading = false;
    }
  }
  async getAdvertiser() {
    this.advertiser = await lastValueFrom(this.advertiserApi.getAdvertiserById(this.advertiserId));
  }

  async getDbConsumerReferralCampaigns() {
    this.referralCampaigns = await lastValueFrom(this.campaignsApi.getConsumerReferralCampaigns(this.campaignId));
    this.bindConsumerReferrals(this.referralCampaigns);
  }


  async setFormValue(referralCampaigns: Campaign[]) {
    if (!referralCampaigns)
      return;

    this.bindConsumerReferrals(referralCampaigns);
  }

  getFormConsumerReferralCampaigns(): Campaign[] {
    if (!this.form.value.consumerReferralCampaigns) return;

    const items = this.form.value.consumerReferralCampaigns.map((f: any, index: number) => new Campaign({
      id: f.id,
      title: f.channel,
      description: f.channel,
      advertiser: this.advertiser,
      advertiserId: this.advertiserId,
      onboardingUrl: f.onboardingUrl,

      strategy: new ConsumerReferralStrategy({
        name: 'ConsumerReferralStrategy',
        refCampaignId: this.campaignId,
        channel: f.channel,
        isDefaultChannel: !!f.isDefaultChannel,
        inviteCode: f.inviteCode,
        joinRewardSender: formToRewardData(f.joinRewardSender),
        joinRewardReceiver: formToRewardData(f.joinRewardReceiver),
        firstConsRewardSender: formToRewardData(f.firstConsRewardSender),
        firstConsRewardReceiver: formToRewardData(f.firstConsRewardReceiver),
        completeRewardSender: formToRewardData(f.completeRewardSender),
        completeRewardReceiver: formToRewardData(f.completeRewardReceiver),
        referralLinkSettings: !!f.isDefaultChannel && this.referralLinkSetting()?.getFormValue(),
        flow: this.dataValidations[index]?.isEnabledCompleteReward ? new PunchCardFlow({ steps: this.getGroupSteps(index) }) : undefined
      })
    }));
    return items;
  }

  // rules reactive array
  addConsumerReferral(referral?: Campaign, strategy?: ConsumerReferralStrategy) {
    const controls = <FormArray>this.form.controls.consumerReferralCampaigns;
    const grp = new FormGroup({
      id: new FormControl(referral?.id),
      channel: new FormControl(strategy?.channel || '', Validators.required),
      isDefaultChannel: new FormControl(strategy?.isDefaultChannel),
      inviteCode: new FormControl(strategy?.inviteCode),
      onboardingUrl: new FormControl(referral?.onboardingUrl),
      joinRewardSender: new FormControl(strategy?.joinRewardSender),
      joinRewardReceiver: new FormControl(strategy?.joinRewardReceiver),
      firstConsRewardSender: new FormControl(strategy?.firstConsRewardSender),
      firstConsRewardReceiver: new FormControl(strategy?.firstConsRewardReceiver),
      completeRewardSender: new FormControl(strategy?.completeRewardSender),
      completeRewardReceiver: new FormControl(strategy?.completeRewardReceiver),
      referralLinkSettings: new FormControl(strategy?.referralLinkSettings)
    });
    controls.push(grp);
    if (!referral?.id) {
      this.selectedChannelIndex = controls.length - 1;
    }
    this.bindingConsumerReferralStepForChannel(referral, controls.length - 1);

  }

  async removeConsumerReferral(index: number) {
    let referralCampaigns = this.getFormConsumerReferralCampaigns();
    const deleted = await this.deleteConsumerReferralCampaign(referralCampaigns[index]);
    if (deleted) {
      referralCampaigns.splice(index, 1);
      this.bindConsumerReferrals(referralCampaigns);
    }

  }

  bindConsumerReferrals(campaigns: Campaign[]) {
    const reactiveControls = <FormArray>this.form.controls.consumerReferralCampaigns;
    reactiveControls.controls.length = 0;
    reactiveControls.patchValue([]);
    this.bindingConsumerReferralSteps(campaigns);
    for (let i = 0; i < campaigns.length; i++) {
      const referral = campaigns[i];
      const strategy = referral.strategy as ConsumerReferralStrategy;;
      this.addConsumerReferral(referral, strategy);
    }
  }

  bindingConsumerReferralSteps(campaigns: Campaign[], flowIndex?: number, previousFlow?: PunchCardStep) {
    for (let i = 0; i < campaigns.length; i++) {
      this.bindingConsumerReferralStepForChannel(campaigns[i], i, flowIndex, previousFlow);
    }
  }

  private bindingConsumerReferralStepForChannel(campaign: Campaign, index: number, flowIndex?: number, previousFlow?: PunchCardStep) {
    const consumerSteps: PunchCardStep[] = this.referralCampaigns[index]?.strategy?.flow?.steps || [];
    const steps = [];
    for (let i = 0; i < this.stepList?.length; i++) {
      const clonedStep = this.stepList[i];
      const step = new PunchCardStep(clonedStep);
      if (consumerSteps[i]?.completeReward?.productReward) {
        if (i == flowIndex && this.isSameProduct(previousFlow?.completeReward, consumerSteps[i].completeReward)) {
          step.completeReward = new RewardData(this.stepList[i].completeReward);
          consumerSteps[i].completeReward = step.completeReward;
        } else {
          step.completeReward = consumerSteps[i].completeReward;
        }
        steps[i] = step;

      }
      else if (this.dataValidations[index]?.steps[i]?.completeReward?.productReward) {
        if (i == flowIndex && this.isSameProduct(previousFlow?.completeReward, this.dataValidations[index]?.steps[i].completeReward)) {
          step.completeReward = new RewardData(this.stepList[i].completeReward);
        } else {
          step.completeReward = this.dataValidations[index]?.steps[i].completeReward;
        }
        steps[i] = step;

      }
      else {
        if (previousFlow?.completeReward?.productReward) {
          delete step?.completeReward?.productReward;
        }
        steps[i] = step;
      }

    }

    this.dataValidations[index] = {
      invalid: false,
      stepValidations: [],
      steps,
      isEnabledCompleteReward: this.checkHasReceiverCompleteReward(campaign)
    };
  }

  async deleteConsumerReferralCampaign(c: Campaign) {
    if (!c.id) return true;    // not in db yet

    const dialogModel = ConfirmDialogModel.danger(
      'Are you sure you want to delete this consumer referral link?',
      'Delete consumer referral link?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) return;

    await lastValueFrom(this.campaignsApi.deleteCampaign(c.id, this.advertiserId));

    this.snackbar.open(`Consumer referral link was deleted!`, null, { duration: 3000 });
    return true;
  }


  onDefaultChannelChanged(ix: number, e: any) {
    const reactiveControls = <FormArray>this.form.controls.consumerReferralCampaigns;
    for (let grp of reactiveControls.controls)
      grp.patchValue({ isDefaultChannel: false });

    reactiveControls.controls[ix].patchValue({ isDefaultChannel: e.checked });
  }


  async getAdminInviteCode(index: number) {
    const campaigns = this.getFormConsumerReferralCampaigns();
    const referral = campaigns[index];
    const adminInviteCode = (referral.strategy as ConsumerReferralStrategy).inviteCode;

    const safeBox = new SafeBox({ code: adminInviteCode });
    await lastValueFrom(this.dialog.open(UniversalCodeComponent, { data: safeBox }).afterClosed());
  }


  isValid() {

    let valid = true;
    const consumerReferralList = this.getFormConsumerReferralCampaigns();
    for (let i = 0; i < consumerReferralList.length; i++) {
      const campaign = consumerReferralList[i];
      let isValid = this.validateStepByGroup(i)
      if (this.punchCardCampaignService.allowFlow) {
        isValid = this.validateStepByGroup(i)
      }
      else {
        isValid = this.punchCardCampaignService.strategyType == PunchCardStrategyType.Stamps ? !!campaign.strategy?.completeRewardReceiver?.productReward : true;
      }

      valid = valid && isValid && this.validateConsumerReferral(campaign.strategy);
    }

    const referralLinkSetting = this.referralLinkSetting();
    if (referralLinkSetting) {
      valid = valid && referralLinkSetting?.isValid();
    }

    return valid && this.form.valid;
  }

  getGroupSteps(groupIndex: number) {
    if (!this.validateStepByGroup(groupIndex)) {
      return undefined;
    }
    const steps: PunchCardStep[] = [];
    const groupedFlows = this.flowItemList().filter(item => item.group == groupIndex);
    for (let i = 0; i < groupedFlows.length; i++) {
      steps.push(groupedFlows[i].getFormValue());
    }
    return steps;
  }

  validateStepByGroup(groupIndex: number) {
    if (this.dataValidations.length == 0 || !this.dataValidations[groupIndex]) {
      return true;
    }

    if (this.punchCardCampaignService.strategyType == PunchCardStrategyType.Stamps && !this.dataValidations[groupIndex]?.isEnabledCompleteReward) {
      return false;
    }

    let validGroup = true;
    const dataValidations = this.dataValidations[groupIndex];
    const groupedFlows = this.flowItemList().filter(item => item.group == groupIndex);
    for (let i = 0; i < groupedFlows.length; i++) {
      dataValidations.stepValidations[i] = groupedFlows[i].isValid();
      validGroup = validGroup && groupedFlows[i].isValid();
    }
    this.dataValidations[groupIndex].invalid = !validGroup;
    return validGroup;
  }

  validateConsumerReferral(strategy: any): boolean {
    const isNegative = (reward: any) => !!reward?.productReward && reward.coinReward?.coins?.toString()?.startsWith('-');

    if (this.punchCardCampaignService.strategyType === PunchCardStrategyType.Points) {
      const checkNegative = (senderOrReceiver: any) =>
        isNegative(senderOrReceiver.joinRewardSender) ||
        isNegative(senderOrReceiver.firstConsRewardSender) ||
        isNegative(senderOrReceiver.completeRewardSender) ||
        isNegative(senderOrReceiver.joinRewardReceiver) ||
        isNegative(senderOrReceiver.firstConsRewardReceiver) ||
        isNegative(senderOrReceiver.completeRewardReceiver);

      return !checkNegative(strategy);
    }

    return true;
  }

  async onSubmit() {
  }

  compareRewardData: ((a: RewardData, b: RewardData) => boolean) | null = (a: RewardData, b: RewardData) => a && b && a.productReward.productSlug === b.productReward.productSlug;

  private checkHasReceiverCompleteReward(campaign: Campaign) {
    if (!campaign) {
      return false;
    }
    if (this.punchCardCampaignService.allowFlow) {
      return !!campaign?.strategy?.flow;
    }

    return this.punchCardCampaignService.strategyType == PunchCardStrategyType.Stamps ? !!campaign.strategy?.completeRewardReceiver?.productReward : true;
  }

  private isSameProduct(product1: RewardData, product2: RewardData) {
    return product1?.productReward && product2?.productReward && product1?.productReward?.productSlug == product2?.productReward?.productSlug;
  }
}


